import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Form, FormikProvider, useFormik } from 'formik';
import { Box, Button, Divider, Grid, InputAdornment, Paper, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import GlobalSelectField from '../../../components/mui/GlobalSelectField';
import DatePickerCustom from '../../../components/DatePicker/DatePickerCustom';
import { Icon } from '@iconify/react/dist/iconify.js';
import Searchbar from '../../../components/Search/Searchbar';
import RightSection from './RightSection';
import LeftSection from './LeftSection';
import { useSelector } from 'react-redux';
import { getAllGoals, getAlltagnames, getGoalBlockStatus, getGoalStatusesForFilterTabs } from '../../../redux/features/appraisalSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import SearchIcon from '@mui/icons-material/Search';
const SmallCapsText = ({ children, sx = {} }) => {
  return (
    <Typography
      sx={{
        mr: 1,
        fontSize: '0.65rem',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

const GoalLandingView = ({ adminView, user }) => {
  const storedCred = JSON.parse(localStorage.getItem('cred')) || {};
  const savedFilters = storedCred?.goalAppraisalFilters;

  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(true);
  const [showProgress, setShowProgress] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { loading, goalData, goalStatuses, goalStatusFilterTabLoading, tagNames, currentUserGoalBlocked } = useSelector(
    (state) => state.appraisal,
  );
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);
  const [expandedGoalsIds, setExpandedGoalsIds] = useState([]);
  const [selectedTabId, SetSelectedTabId] = useState(-1);
  const searchRef = useRef(null);

  const defaultInitialValues = {
    tags: '',
    search: '',
    archiveViewChecked: false,
    fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    type: adminView ? 2 : 1,
  };

  const initialValues = savedFilters
    ? { ...defaultInitialValues, ...savedFilters, type: adminView ? 2 : 1 }
    : defaultInitialValues;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: (values) => {
      const updatedCred = {
        ...storedCred,
        goalAppraisalFilters: values,
      };
      localStorage.setItem('cred', JSON.stringify(updatedCred));
      dispatch(getGoalStatusesForFilterTabs(dispatchData));
      dispatch(getAllGoals(dispatchData));
    },
  });

  const dispatchData = {
    ...formik.values,
    goalStatus: selectedTabId,
    user: { userID: user?.userID ? user?.userID : -1 },
  };

  useEffect(() => {
    // dispatch(getGoalStatusesForFilterTabs(dispatchData));
    dispatch(getAlltagnames());
  }, []);

  useEffect(() => {
    dispatch(getGoalStatusesForFilterTabs(dispatchData));
    // dispatch(getAlltagnames());
  }, [user?.userID]);

  // Find out whether the target user is blocked from adding goals. When a
  // specific user is selected (admin view) we check that user; otherwise the
  // backend defaults to the logged-in user (their own Goal tab).
  useEffect(() => {
    dispatch(getGoalBlockStatus({ userId: user?.userID }));
  }, [user?.userID]);

  const clearFilters = () => {
    formik.resetForm({ values: { ...defaultInitialValues } });
    const storedCred = JSON.parse(localStorage.getItem('cred')) || {};
    delete storedCred.goalAppraisalFilters;
    localStorage.setItem('cred', JSON.stringify(storedCred));
    SetSelectedTabId(-1);
    if (searchRef.current) {
      searchRef.current.value = '';
    }
    const clearedDispatchData = {
      ...defaultInitialValues,
      goalStatus: -1, // reset if needed
      user: { userID: user?.userID ? user?.userID : -1 },
    };
    dispatch(getGoalStatusesForFilterTabs(clearedDispatchData));
    dispatch(getAllGoals(clearedDispatchData));
  };

  return (
    <>
      {/* FILTER SECTION  STARTS >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>*/}
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            sx={{
              minWidth: { xs: '111px', sm: '120px' },
              minHeight: 30,
              fontSize: {
                xs: '0.56rem',
                sm: '0.65rem',
              },
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
              },
            }}
            variant={'outlined'}
            color="primary"
            onClick={() => setShowFilters((prev) => !prev)}
            size="small"
            startIcon={showFilters ? <Icon icon="icon-park-solid:up-c" /> : <Icon icon="icon-park-solid:down-c" />}
          >
            {showFilters ? 'HIDE FILTERS' : 'SHOW FILTERS'}
          </Button>
          <Button
            sx={{
              minWidth: '138px',
              minHeight: 30,
              fontSize: {
                xs: '0.56rem',
                sm: '0.65rem',
              },
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
              },
            }}
            variant={'outlined'}
            color="primary"
            onClick={() => setShowProgress((prev) => !prev)}
            size="small"
            startIcon={showProgress ? <Icon icon="icon-park-solid:up-c" /> : <Icon icon="mdi:graph-line" />}
          >
            {showProgress ? 'HIDE PROGRESS' : 'SHOW PROGRESS'}
          </Button>
          {/* When an admin has blocked this user (Appraisal Operations > Config),
              the "Add Goal" button is disabled. The Tooltip explains why. The
              button is wrapped in a <span> because MUI does not fire tooltips on
              disabled buttons.

              currentUserGoalBlocked is null while the status is still loading, and
              we keep the button disabled until it is definitely false. Otherwise a
              blocked user sees a working button for a moment and can click through
              before their status arrives. */}
          <Tooltip
            title={
              currentUserGoalBlocked === true
                ? 'Adding goals has been blocked by the admin.'
                : ''
            }
            arrow
          >
            <span>
              <Button
                sx={{
                  minHeight: 30,
                  fontSize: {
                    xs: '0.56rem',
                    sm: '0.65rem',
                  },
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                  },
                }}
                variant={'outlined'}
                color="primary"
                // Only enabled once we know for sure the user is allowed.
                disabled={currentUserGoalBlocked !== false}
                onClick={() =>
                  navigate('/Appraisal-Management/add-goal', {
                    state: { adminView: adminView, user: user },
                  })
                }
                size="small"
                startIcon={<Icon icon="gridicons:add" />}
              >
                ADD GOAL
              </Button>
            </span>
          </Tooltip>
          <Button
            sx={{
              minHeight: 30,
              fontSize: {
                xs: '0.56rem',
                sm: '0.65rem',
              },
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
              },
            }}
            variant={'outlined'}
            color="primary"
            onClick={clearFilters}
            size="small"
            startIcon={<Icon icon="mdi:clear" />}
          >
            CLEAR FILTERS
          </Button>
        </Box>

        <AnimatePresence initial={false}>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              {/* FILTER SECTION */}
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Paper sx={{ mt: 2 }}>
                    <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Grid item xs={12} sm={6} md={2}>
                        <DatePickerCustom
                          label={'From Date'}
                          value={dayjs(formik.values.fromDate)}
                          setValue={(date) => formik.setFieldValue('fromDate', date.format('YYYY-MM-DD'))}
                          viewDetails={['year', 'month', 'day']}
                          fullWidth
                          disabled={false}
                          height={36}
                          fontSize={'0.75rem'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <DatePickerCustom
                          label={'To Date'}
                          value={dayjs(formik.values.toDate)}
                          setValue={(date) => formik.setFieldValue('toDate', date.format('YYYY-MM-DD'))}
                          viewDetails={['year', 'month', 'day']}
                          fullWidth
                          disabled={false}
                          height={36}
                          fontSize={'0.75rem'}
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={6} md={2.5}>
                        <Box sx={{ mt: '3px' }}>
                          <GlobalSelectField
                            height={36}
                            fontSize={'0.75rem'}
                            options={{
                              formik: formik,
                              label: 'Tags',
                              name: 'tags',
                              data: tagNames || [],
                              keys: {
                                id: 'appraisal_tag_id',
                                value: 'appraisal_tag_name',
                              },
                              sx: {
                                width: '100%',
                              },
                              InputProps: {
                                endAdornment: formik.values.tags ? (
                                  <InputAdornment position="end" sx={{ position: 'absolute', right: '25px' }}>
                                    <Icon
                                      icon="mdi:close-circle"
                                      style={{ cursor: 'pointer', fontSize: '1rem', color: 'gray' }}
                                      onClick={() => formik.setFieldValue('tags', '')}
                                    />
                                  </InputAdornment>
                                ) : null,
                              },
                            }}
                          />
                        </Box>
                      </Grid> */}
                      <Grid item xs={12} sm={6} md={2.5}>
                        <Box
                          sx={{
                            mt: '3px',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <GlobalSelectField
                            height={36}
                            fontSize={'0.75rem'}
                            options={{
                              formik: formik,
                              label: 'Quarters',
                              name: 'tags',
                              data: tagNames || [],
                              keys: {
                                id: 'appraisal_tag_id',
                                value: 'appraisal_tag_name',
                              },
                              sx: {
                                width: '100%',
                              },
                            }}
                          />

                          {formik.values.tags && (
                            <Box
                              sx={{
                                position: 'absolute',
                                right: '32px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Icon
                                icon="mdi:close-circle"
                                width={18}
                                height={18}
                                style={{
                                  cursor: 'pointer',
                                  color: 'gray',
                                }}
                                onClick={() => formik.setFieldValue('tags', '')}
                              />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={2.5}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {/* <Searchbar
                          height={36}
                          fontSize={"0.75rem"}
                          value={formik.values.search}
                          setValue={(value) =>
                            formik.setFieldValue("search", value)
                          }
                          label={"Search"}
                          searchIconDisabled={true}
                        /> */}
                        <TextField
                          inputRef={searchRef}
                          variant="outlined"
                          size="small"
                          fullWidth
                          name="search"
                          onBlur={(e) => formik.setFieldValue('search', e.target.value)}
                          placeholder="Search"
                          InputProps={{
                            style: { height: 36, fontSize: '0.75rem' },
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={2}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box display={'flex'} flexDirection={'row'} gap={1}>
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              px: 1,
                              boxShadow: 'none',
                              height: { xs: 28, sm: 'auto' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                sx={{
                                  mr: 1,
                                  fontSize: {
                                    xs: '0.62rem',
                                    sm: '0.65rem',
                                  },
                                  textTransform: 'uppercase',
                                  fontWeight: 'bold',
                                }}
                              >
                                Archive
                              </Typography>
                              <Switch
                                size="small"
                                checked={formik.values.archiveViewChecked}
                                onChange={(e) => formik.setFieldValue('archiveViewChecked', e.target.checked)}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            </Box>
                          </Button>

                          <Button
                            size="small"
                            type="submit"
                            variant="outlined"
                            color="primary"
                            sx={{
                              height: { xs: 28, sm: 'auto' },
                              fontSize: {
                                xs: '0.62rem',
                                sm: '0.65rem',
                              },
                              px: 3,
                              boxShadow: 'none',
                            }}
                          >
                            SUBMIT
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Form>
              </FormikProvider>
              <Divider sx={{ mt: 1 }} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
      {/* FILTER SECTION END  <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<*/}
      {/* CARD SECTION */}
      {/* <Box mt={showFilters ? 0 : 1}>
        <CardSection cardDatas={cardDatas} />
      </Box> */}
      {/* CARD SECTION */}
      {/* BODY SECTION START >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>*/}
      <Box sx={{ mt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={showProgress ? 8 : 12}>
            <LeftSection
              data={goalData}
              loading={loading}
              api={getAllGoals}
              dispatchData={dispatchData}
              selectedGoalIds={selectedGoalIds}
              setSelectedGoalIds={setSelectedGoalIds}
              expandedGoalsIds={expandedGoalsIds}
              setExpandedGoalsIds={setExpandedGoalsIds}
              tabOptions={goalStatuses}
              selectedTabId={selectedTabId}
              SetSelectedTabId={SetSelectedTabId}
              goalStatusFilterTabLoading={goalStatusFilterTabLoading}
              dispatch={dispatch}
              enqueueSnackbar={enqueueSnackbar}
              adminView={adminView}
              user={user}
            />
          </Grid>
          {showProgress && (
            <Grid item xs={12} md={4}>
              <RightSection data={goalData} loading={loading} />
            </Grid>
          )}
        </Grid>
      </Box>
      {/* BODY SECTION END <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
    </>
  );
};

export default GoalLandingView;
